import { useEffect, useMemo, useState } from "react";
import { useFavorites } from "@/contexts/FavoritesContext";
import type { AppItem } from "@/types/appItem";
import AppCard from "./AppCard";
import SearchBar from "@/ui/SearchBar";
import Grid from "@/ui/Grid";
import { trackEvent } from "@/services/analyticsService";

interface AppGalleryProps {
  appItems: AppItem[];
}

const AppGallery = ({ appItems }: AppGalleryProps) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [searchQuery, setSearchQuery] = useState("");

  const filteredApps = useMemo(() => {
    if (!searchQuery.trim()) {
      return appItems;
    }

    const query = searchQuery.toLowerCase();
    return appItems.filter(
      (app) =>
        app.name.toLowerCase().includes(query) ||
        app.description?.toLowerCase().includes(query) ||
        app.category?.toLowerCase().includes(query),
    );
  }, [appItems, searchQuery]);

  useEffect(() => {
    if (!searchQuery.trim()) return;
    const timer = setTimeout(() => {
      trackEvent("app_search", {
        query: searchQuery.trim(),
        result_count: filteredApps.length,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, filteredApps.length]);

  const sortedApps = useMemo(() => {
    return [...filteredApps].sort((a, b) => {
      const aFav = isFavorite(a.id);
      const bFav = isFavorite(b.id);
      // Favorites first
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      // Apps with URLs before "Coming Soon" appItems
      const aHasUrl = Boolean(a.url);
      const bHasUrl = Boolean(b.url);
      if (aHasUrl && !bHasUrl) return -1;
      if (!aHasUrl && bHasUrl) return 1;
      // Alphabetical
      return a.name.localeCompare(b.name);
    });
  }, [filteredApps, isFavorite]);

  return (
    <div className="flex flex-1 flex-col">
      {/* Search bar - stays visible */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search apps..."
        />
      </div>

      {/* Scrollable grid area - pt-2 and pb-4 give space for card shadows/glow */}
      <div className="-mx-4 -mt-2 flex-1 overflow-y-auto px-4 pt-2 pb-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 xl:-mx-12 xl:px-12">
        <Grid>
          {sortedApps.map((app) => (
            <AppCard
              key={app.id}
              app={app}
              isFavorite={isFavorite(app.id)}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </Grid>
      </div>
    </div>
  );
};

export default AppGallery;
